import React from 'react';
import { css } from '@emotion/react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'components/Button';
import { FormControl } from 'components/FormControl';
import { FormFooter } from 'components/FormFooter';
import { FormLine } from 'components/FormLine';
import { FormSection } from 'components/FormSection';
import { FormSectionHeading } from 'components/FormSectionHeading';
import { Input } from 'components/Input';
import { Heading } from '../../../components/Heading';
import { SummaryHeading } from '../../../components/SummaryHeading';
import { SummaryLine } from '../../../components/SummaryLine';
import { SummarySeparator } from '../../../components/SummarySeparator';
import { useStep } from '../hooks/useStep';
import { useMutation, useQuery } from 'urql';
import {
	MUTATION_CONFIRM_REQUEST,
	QUERY_REQUEST,
	QUERY_REQUEST_STATE,
} from 'queries/queries-booking';

import { format } from 'date-fns';
import { fieldsToView, FIELD_TO_VIEW, getExternalUrl, getFormattedPrice } from 'utils/utils';
import { emailValidation, nonEmptyValidation, phoneValidation } from '../forms/validations';
import { IconState } from 'components/ProcessStep';

import { IconEmail } from 'icons/icon-email';
import { theme } from 'styles/theme';
import { SpinnerBlock } from 'components/SpinnerBlock';
import { SummaryPrice } from 'screens/booker/screens/request/drawer/SummaryPrice';

import { LinkExternal } from 'components/LinkExternal';
import { OptionalInfoBox } from '../components/OptionalInfoBox';
import { PhoneInput } from 'components/PhoneInput';
import { FEE_TYPE } from './FeeStep';
import { Tooltip, TooltipDirection } from 'components/Tooltip';
import { Badge } from 'components/Badge';
import { useRequestStateQuery } from 'generated/graphql';

enum FORM_SUMMARY {
	NAME = 'confirmed_name',
	EMAIL = 'confirmed_email',
	PHONE = 'confirmed_phone',
}

export const SummaryStep = () => {
	const { id } = useStep();

	const [requestResult] = useQuery({
		query: QUERY_REQUEST,
		variables: {
			url: id,
		},
	});

	const [_stateRequest] = useRequestStateQuery({
		variables: { url: id },
	});

	const { fetching, data, error } = requestResult;

	return (
		<div
			css={css`
				width: 560px;
			`}
		>
			<Heading
				title="Shrnutí a odeslání"
				desc={
					<>
						<OptionalInfoBox finalStep />
					</>
				}
			/>

			{fetching && <SpinnerBlock />}
			{!fetching && (
				<>
					<SummaryHeading>Booking Interpreta</SummaryHeading>
					<SummaryOverview />
					<SummarySeparator />

					<Details />

					{!data.request.confirmed && (
						<>
							<FormSectionHeading>Vyplnil</FormSectionHeading>
							<Form />
						</>
					)}
				</>
			)}
		</div>
	);
};

const SummaryOverview = () => {
	const { id } = useStep();

	// Get information about event
	const [requestResult] = useQuery({
		query: QUERY_REQUEST,
		variables: {
			url: id,
		},
	});

	const { fetching, data, error } = requestResult;

	const totalPrice = data?.request?.fee_amount + data?.request?.fee_travel;

	return (
		<>
			{fetching && <SpinnerBlock />}

			{!fetching && (
				<>
					<SummaryLine label="Interpret">{data?.request?.artist?.name}</SummaryLine>
					<SummaryLine label="Datum">
						{format(new Date(data?.request?.event_date), 'dd. MM. YYY')}
					</SummaryLine>
					<SummaryLine label="Město">{data?.request?.event_address_city}</SummaryLine>
					<SummaryLine label="Částka">
						{data?.request?.fee_type === FEE_TYPE.FIXED && (
							<strong>{getFormattedPrice(data?.request?.fee_total, data?.request?.fee_vat)}</strong>
						)}

						{data?.request?.fee_type === FEE_TYPE.OTHER && (
							<div
								css={css`
									display: inline-flex;
								`}
							>
								<Tooltip content={data?.request?.fee_other} direction={TooltipDirection.BOTTOM}>
									<strong
										css={css`
											text-decoration: underline dotted;
										`}
									>
										Jiné podmínky
									</strong>
								</Tooltip>
							</div>
						)}

						{/* <SummaryPrice value={totalPrice} isVAT={data?.request?.fee_vat} /> */}
					</SummaryLine>
					<SummaryLine label="Technický rider">
						{data?.request?.artist?.rider_link ? (
							<LinkExternal href={data?.request?.artist?.rider_link}>
								<strong>Otevřít</strong>
							</LinkExternal>
						) : (
							<>Není stodupný. Kontaktujte bookera.</>
						)}
					</SummaryLine>
				</>
			)}
		</>
	);
};

const Form = () => {
	const { id, goPrevStep } = useStep();

	const {
		handleSubmit,
		register,
		control,
		formState: { errors },
	} = useForm();

	const labelWidth = 178;

	// Submit Form
	const [confirmRequest, executeConfirmRequest] = useMutation(MUTATION_CONFIRM_REQUEST);

	const onSubmit = (data) => {
		executeConfirmRequest({
			url: id,
			input: data,
		}).then((result) => {
			// console.log(result);

			if (result?.error) {
				// VYHODIT ERROR
				return false;
			}
		});
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormSection>
				<FormLine first>
					<FormControl
						required
						label="Jméno"
						htmlFor={FORM_SUMMARY.NAME}
						labelWidth={labelWidth}
						error={errors[FORM_SUMMARY.NAME]}
					>
						<Input
							placeholder="Zadejte celé jméno..."
							{...register(
								FORM_SUMMARY.NAME,
								nonEmptyValidation({ required: 'Zadejte celé jméno.' }),
							)}
							error={!!errors[FORM_SUMMARY.NAME]}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="E-mail"
						htmlFor={FORM_SUMMARY.EMAIL}
						labelWidth={labelWidth}
						error={errors[FORM_SUMMARY.EMAIL]}
					>
						<Input
							icon={<IconEmail />}
							{...register(FORM_SUMMARY.EMAIL, emailValidation({ required: 'Zadejte e-mail.' }))}
							error={!!errors[FORM_SUMMARY.EMAIL]}
							width={240}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="Telefonní číslo"
						htmlFor={FORM_SUMMARY.PHONE}
						labelWidth={labelWidth}
						error={errors[FORM_SUMMARY.PHONE]}
					>
						<Controller
							name={FORM_SUMMARY.PHONE}
							control={control}
							rules={{
								...phoneValidation({ required: true }),
							}}
							render={({ field }) => {
								return <PhoneInput {...field} width={240} error={!!errors[FORM_SUMMARY.PHONE]} />;
							}}
						/>
					</FormControl>
				</FormLine>
			</FormSection>

			<div
				css={css`
					color: ${theme.color.text['300']};
					font-size: 13px;
				`}
			>
				<strong
					css={css`
						color: ${theme.color.text['400']};
					`}
				>
					Poznámka:
				</strong>{' '}
				Vyplněním tohoto formuláře potvrzuji, že jsem četl(a) rider umělce, a předal(a) jsem jej
				osobám/organizacím zodpovědným za technické zabezpečení koncertu. Dále potvrzuji, že jsem
				seznámen(a) se skutečností, že nedodržení podmínek uvedených v rideru nebo žádná či
				nedostatečná konzultace s umělcem/agenturou (či osobou uvedenou v rideru jako kontakt) v
				dostatečném časovém předstihu, může vést k nekonání koncertu.
			</div>

			<FormFooter>
				<Button outline type="button" onClick={() => goPrevStep()}>
					Zpět
				</Button>
				<Button type="submit" isProcessing={confirmRequest.fetching}>
					Potvrzuji, odeslat
				</Button>
			</FormFooter>
		</form>
	);
};

const Details = () => {
	const { id, step } = useStep();

	const [requestDetail, executeRequestDetail] = useQuery({
		query: QUERY_REQUEST,
		variables: { url: id },
	});

	const { fetching, data, error } = requestDetail;

	return (
		<>
			{!!fetching && <SpinnerBlock />}
			{!fetching && (
				<div
					css={css`
						width: 100%;
					`}
				>
					{fieldsToView.map((section, key) => {
						if (!!section.dependsOn) {
							// console.log('Section depends on field', section.dependsOn);
							// console.log(section.dependsOn.property, data.request[section.dependsOn.property]);

							if (data.request[section.dependsOn.property] !== section.dependsOn.value) return;
						}

						return <DetailSection section={section} data={data.request} key={key} />;
					})}
				</div>
			)}
		</>
	);
};

const DetailSection = ({ section, data }) => {
	return (
		<>
			<SummaryHeading>{section.title}</SummaryHeading>

			{section.fields.map(
				({ key, name, type, enumOptions, showVATCalculation, dependsOn }, index) => {
					if (dependsOn) {
						// console.log('Item depends on field', dependsOn);

						if (data[dependsOn.property] !== dependsOn.value) {
							return;
						}
					}

					return (
						<SummaryLine key={index} label={name}>
							{data[key] ||
							type === FIELD_TO_VIEW.BOOLEAN ||
							type === FIELD_TO_VIEW.PRICE ||
							(type === FIELD_TO_VIEW.DISTANCE && data[key] !== null) ? (
								<>
									{type === FIELD_TO_VIEW.BOOLEAN && <>{data[key] ? 'Ano' : 'Ne'}</>}
									{type === FIELD_TO_VIEW.DATE && <>{format(new Date(data[key]), 'dd. MM. YYY')}</>}
									{type === FIELD_TO_VIEW.DISTANCE && <>{data[key]} km</>}
									{type === FIELD_TO_VIEW.ENUM && <>{enumOptions[data[key]]}</>}
									{type === FIELD_TO_VIEW.PRICE && (
										<SummaryPrice
											value={data[key]}
											isVAT={data.fee_vat}
											showVATCalculation={showVATCalculation}
										/>
									)}
									{type === FIELD_TO_VIEW.LINK && (
										<LinkExternal href={getExternalUrl(data[key])}>{data[key]}</LinkExternal>
									)}
									{type === FIELD_TO_VIEW.STRING && <>{data[key]}</>}
									{type === FIELD_TO_VIEW.EMAIL && (
										<LinkExternal href={'mailto:' + data[key]}>{data[key]}</LinkExternal>
									)}
									{type === FIELD_TO_VIEW.TIME && (
										<>{format(new Date('2020-01-01 ' + data[key]), 'HH:mm')}</>
									)}
								</>
							) : (
								<div
									css={css`
										display: flex;
										align-items: center;
									`}
								>
									<IconState isIncomplete sizeSmall />

									<strong
										css={css`
											font-weight: 600;
											padding-left: 8px;
										`}
									>
										Nutno doplnit
									</strong>
								</div>
							)}
						</SummaryLine>
					);
				},
			)}

			<SummarySeparator />
		</>
	);
};
